import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  EmptyState,
  LoadingState,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { API_VERSION_PREFIX, apiErrorMessage, newIdempotencyKey } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { Panel } from "@/modules/hub-kit/HubKit";

/**
 * Creators › Applications › Candidates (i149, Play Polishing 18.3).
 *
 * People who reached 1,000 accepted followers on HUDDL. Each also opens a
 * Creator Operations task. Vet the account by hand, then Invite (they get an
 * Inbox notice to apply as a Verified Creator; they still apply like anyone
 * else) or Pass. Either closes the task. A person is flagged once.
 */

type Candidate = {
  user_id: string;
  display_name: string | null;
  handle: string | null;
  followers_now: number;
  followers_at_flag: number;
  flagged_at: string;
  status: "new" | "invited" | "passed";
  decided_at: string | null;
  note: string | null;
  has_open_application: boolean;
};

const FILTERS = [
  { id: "new", label: "Waiting" },
  { id: "invited", label: "Invited" },
  { id: "passed", label: "Passed" },
  { id: "all", label: "All" },
] as const;

const BASE = `${API_VERSION_PREFIX}/hq/creators/candidates`;
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export function CreatorCandidatesTab() {
  const client = useApiClient();
  const toast = useToast();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("new");
  const [rows, setRows] = useState<Candidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await client.get<{ candidates: Candidate[] }>(`${BASE}?status=${filter}`);
      setRows(res.candidates);
    } catch (e) {
      setError(apiErrorMessage(e));
      setRows([]);
    }
  }, [client, filter]);

  useEffect(() => { setRows(null); void load(); }, [load]);

  const decide = useCallback(async (row: Candidate, decision: "invite" | "pass") => {
    setBusy(row.user_id);
    try {
      await client.post(`${BASE}/${row.user_id}/${decision}`, {}, newIdempotencyKey());
      toast.push({
        title: decision === "invite" ? `Invited ${row.display_name ?? "them"} to apply` : `Passed on ${row.display_name ?? "them"}`,
        tone: "success",
      });
      await load();
    } catch (e) {
      toast.push({ title: apiErrorMessage(e), tone: "danger" });
    } finally {
      setBusy(null);
    }
  }, [client, load, toast]);

  const body = useMemo(() => {
    if (rows === null) return <LoadingState />;
    if (rows.length === 0) {
      return (
        <EmptyState
          title={filter === "new" ? "Nobody waiting" : "None yet"}
          description="Someone appears here when they reach 1,000 followers on HUDDL. A task also opens for Creator Operations."
        />
      );
    }
    return (
      <Table>
        <Thead>
          <Tr>
            <Th>Person</Th>
            <Th>Followers now</Th>
            <Th>Flagged</Th>
            <Th>Status</Th>
            <Th>Decide</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((r) => (
            <Tr key={r.user_id}>
              <Td>
                <Link to={`/app/users/${r.user_id}`}>{r.display_name ?? "Unnamed"}</Link>
                {r.handle ? <Text role="small" color="secondary" as="p">@{r.handle}</Text> : null}
                {r.has_open_application ? <Text role="small" color="secondary" as="p">Has an open creator application</Text> : null}
              </Td>
              <Td>{r.followers_now.toLocaleString()}</Td>
              <Td>{when(r.flagged_at)}<Text role="small" color="secondary" as="p">at {r.followers_at_flag.toLocaleString()}</Text></Td>
              <Td>{r.status === "new" ? "Waiting" : r.status === "invited" ? `Invited ${when(r.decided_at)}` : `Passed ${when(r.decided_at)}`}</Td>
              <Td>
                {r.status === "new" ? (
                  <Flex gap={8}>
                    <Button size="sm" disabled={busy !== null} onClick={() => void decide(r, "invite")}>Invite to apply</Button>
                    <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void decide(r, "pass")}>Pass</Button>
                  </Flex>
                ) : "—"}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  }, [rows, filter, busy, decide]);

  return (
    <Stack gap={16}>
      <Panel
        title="Creator candidates"
        description="People who reached 1,000 followers on HUDDL. Open their profile, check the account is real and fits HUDDL, then invite them to apply as a Verified Creator, or pass. They still apply like anyone else."
      >
        <Flex gap={8}>
          {FILTERS.map((f) => (
            <Button key={f.id} size="sm" variant={filter === f.id ? "primary" : "secondary"} onClick={() => setFilter(f.id)}>
              {f.label}
            </Button>
          ))}
        </Flex>
        {error ? <Alert tone="danger" title="Couldn't load candidates">{error}</Alert> : null}
        {body}
      </Panel>
    </Stack>
  );
}
